import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { invoicesAPI, contractsAPI, transactionsAPI } from './api';

const PAYMENT_COLORS = { paid: 'pay-paid', partial: 'pay-partial', unpaid: 'pay-unpaid' };
const PAYMENT_LABELS = { paid: 'Paid', partial: 'Partial', unpaid: 'Unpaid' };

const amount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const fmt = (value, currency = 'INR') => {
  try {
    return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency',
      currency: currency || 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount(value));
  } catch {
    return `${currency || 'INR'} ${amount(value).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
};

const fmtShort = (value) => {
  const v = Math.abs(amount(value));
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};

const fmtDate = (value, fallback = 'Ongoing') =>
  value
    ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : fallback;

const toDateInputValue = (value) => (value ? String(value).split('T')[0] : '');

const parseInvoiceItems = (items) => {
  if (Array.isArray(items)) {
    return items.map((item, index) => ({
      id: item?.id ?? index + 1,
      description: item?.description || '',
      amount: item?.amount ?? '',
    }));
  }
  if (!items) return [];

  try {
    const parsed = JSON.parse(items);
    return Array.isArray(parsed)
      ? parsed.map((item, index) => ({
          id: item?.id ?? index + 1,
          description: item?.description || '',
          amount: item?.amount ?? '',
        }))
      : [];
  } catch {
    return [];
  }
};

const normalizeInvoice = (record) => ({
  id:             record.id,
  invoiceNumber:  record.invoiceNumber || record.invoice_number || '',
  invoiceDate:    toDateInputValue(record.invoiceDate || record.invoice_date),
  clientName:     record.clientName || record.client_name || '',
  total:          amount(record.total),
  gstAmount:      amount(record.gstAmount || record.gst_amount),
  subtotal:       amount(record.subtotal),
  paymentStatus:  record.paymentStatus || record.payment_status || 'unpaid',
  paymentDate:    record.paymentDate || record.payment_date || '',
  paymentAmount:  amount(record.paymentAmount || record.payment_amount),
  paymentMode:    record.paymentMode || record.payment_mode || '',
  paymentNotes:   record.paymentNotes || record.payment_notes || '',
  tdsAmount:      amount(record.tdsAmount || record.tds_amount),
  tdsRate:        amount(record.tdsRate || record.tds_rate),
  currency:       record.currency || 'INR',
  contractId:     record.contractId || record.contract_id || null,
  agreementNumber:record.agreementNumber || record.agreement_number || '',
  placeOfSupply:  record.placeOfSupply || record.place_of_supply || '',
  items:          parseInvoiceItems(record.items),
  prefix:         record.prefix || '',
});

const isDomesticInvoice = (invoice) => (invoice.currency || 'INR') === 'INR';
const roundMoney = (value) => Math.round(amount(value) * 100) / 100;
const deriveTdsRate = (tdsAmount, subtotal) => {
  const base = amount(subtotal);
  if (base <= 0 || amount(tdsAmount) <= 0) return 0;
  return (amount(tdsAmount) / base) * 100;
};

const daysUntil = (value) => {
  if (!value) return null;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
};

const currentValueFor = (contract) => amount(contract.current_contract_value || contract.contract_value);
const invoicedAmountFor = (contract) => amount(contract.invoiced_amount);
const remainingValueFor = (contract) => currentValueFor(contract) - invoicedAmountFor(contract);
const billedPctFor = (contract) => {
  const currentValue = currentValueFor(contract);
  if (currentValue <= 0) return 0;
  return Math.min((invoicedAmountFor(contract) / currentValue) * 100, 100);
};

const isExpiringSoon = (contract, withinDays = 45) => {
  if (contract.status !== 'active') return false;
  const days = daysUntil(contract.revised_end_date || contract.end_date);
  return days !== null && days >= 0 && days <= withinDays;
};

const attentionFlagsFor = (contract) => {
  const flags = [];
  const remaining = remainingValueFor(contract);
  if (contract.status === 'draft') flags.push('Draft contract');
  if (contract.status === 'active' && !Number(contract.milestone_count)) flags.push('Add milestones');
  if (contract.status === 'active' && !Number(contract.deliverable_count)) flags.push('Add deliverables');
  if (contract.status === 'active' && !Number(contract.document_count)) flags.push('Add documents');
  if (contract.status === 'active' && remaining < 0) flags.push('Over ceiling');
  if (isExpiringSoon(contract)) flags.push('Renew soon');
  return flags;
};

/* ─── Chart helpers ──────────────────────────────────────────────── */

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function MonthlyBarChart({ data }) {
  const [hovered, setHovered] = useState(null);
  if (!data.length) return <div className="db-chart-empty">No invoice data yet</div>;

  const maxVal = Math.max(...data.map(d => d.invoiced), 1);
  const W = 340, H = 160;
  const PAD = { l: 52, r: 8, t: 12, b: 26 };
  const plotW = W - PAD.l - PAD.r;
  const plotH = H - PAD.t - PAD.b;
  const logFloor = Math.max(Math.floor(Math.log10(maxVal)), 0);
  const step     = Math.pow(10, logFloor);
  const niceMax  = Math.ceil(maxVal / step) * step;
  const barW     = Math.min((plotW / data.length) - 6, 34);
  const slotW    = plotW / data.length;

  const fmtY = v =>
    v >= 1e7  ? `₹${(v/1e7).toFixed(1)}Cr`
    : v >= 1e5 ? `₹${(v/1e5).toFixed(0)}L`
    : v >= 1e3 ? `₹${(v/1e3).toFixed(0)}K`
    : `₹${v}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" className="db-chart-svg" overflow="visible">
      {/* Y-grid + labels */}
      {[0,1,2,3,4].map(i => {
        const v = (niceMax / 4) * i;
        const y = PAD.t + plotH - (v / niceMax) * plotH;
        return (
          <g key={i}>
            <line x1={PAD.l} y1={y} x2={W - PAD.r} y2={y} stroke="#e2e8f0" strokeWidth="0.5" />
            <text x={PAD.l - 4} y={y + 3.5} fontSize="8.5" textAnchor="end" fill="#94a3b8">{fmtY(v)}</text>
          </g>
        );
      })}
      {/* Baseline */}
      <line x1={PAD.l} y1={PAD.t+plotH} x2={W-PAD.r} y2={PAD.t+plotH} stroke="#cbd5e1" strokeWidth="1" />

      {/* Bars */}
      {data.map((d, i) => {
        const x   = PAD.l + i * slotW + (slotW - barW) / 2;
        const bH  = Math.max((d.invoiced / niceMax) * plotH, 2);
        const y   = PAD.t + plotH - bH;
        const cH  = d.collected > 0 ? Math.max((d.collected / niceMax) * plotH, 0) : 0;
        const cy  = PAD.t + plotH - cH;
        const isH = hovered === i;

        return (
          <g key={i} style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
            {/* Invoiced bar */}
            <rect x={x} y={y} width={barW} height={bH}
              fill={isH ? '#1e3a6e' : '#1e5fa8'} rx="3" />
            {/* Collected overlay */}
            {cH > 0 && (
              <rect x={x} y={cy} width={barW} height={cH}
                fill="#10b981" rx="3" opacity="0.8" pointerEvents="none" />
            )}
            {/* Month label */}
            <text x={x + barW / 2} y={H - 8} fontSize="8.5" textAnchor="middle" fill="#64748b">{d.label}</text>
            {/* Tooltip */}
            {isH && (
              <g>
                <rect x={x - 20} y={Math.max(y - 42, 0)} width={barW + 40} height={36} rx="5" fill="#1e293b" opacity="0.93" />
                <text x={x + barW/2} y={Math.max(y - 27, 15)} fontSize="9" fontWeight="600" fill="white" textAnchor="middle">{d.key}</text>
                <text x={x + barW/2} y={Math.max(y - 15, 28)} fontSize="8" fill="#94a3b8" textAnchor="middle">
                  Inv: {fmtY(Math.round(d.invoiced))} · Rcvd: {fmtY(Math.round(d.collected))}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function MonthlyBarLegend() {
  return (
    <div className="db-chart-legend">
      <span className="db-cl-dot" style={{background:'#1e5fa8'}}/>Invoiced
      <span className="db-cl-dot" style={{background:'#10b981'}}/>Collected
    </div>
  );
}

function StatusDonut({ data }) {
  const [hovered, setHovered] = useState(null);
  const total = data.reduce((s, d) => s + d.amount, 0);
  const CX = 80, CY = 80, R = 65, r = 44;

  if (total <= 0) return <div className="db-chart-empty">No invoices yet</div>;

  let angle = -Math.PI / 2;
  const segs = data
    .filter(d => d.amount > 0)
    .map(d => {
      const start = angle;
      const sweep = (d.amount / total) * 2 * Math.PI;
      angle += sweep;
      return { ...d, start, sweep };
    });

  const arc = (ox, oy, ro, ri, s, e) => {
    const c = Math.cos, sn = Math.sin;
    const x1 = ox + ro*c(s), y1 = oy + ro*sn(s);
    const x2 = ox + ro*c(e), y2 = oy + ro*sn(e);
    const x3 = ox + ri*c(e), y3 = oy + ri*sn(e);
    const x4 = ox + ri*c(s), y4 = oy + ri*sn(s);
    const lg = (e - s) > Math.PI ? 1 : 0;
    return `M${x1},${y1} A${ro},${ro},0,${lg},1,${x2},${y2} L${x3},${y3} A${ri},${ri},0,${lg},0,${x4},${y4}Z`;
  };

  const hovSeg = hovered !== null ? segs.find(s => s.label === hovered) : null;
  const ctrCount = hovSeg ? hovSeg.count : data.reduce((s, d) => s + d.count, 0);

  return (
    <div className="db-donut-wrap">
      <svg viewBox="0 0 160 160" width="150" height="150" style={{flexShrink:0, overflow:'visible'}}>
        {segs.map((seg, i) => (
          <path key={i}
            d={arc(CX, CY, R, r, seg.start, seg.start + seg.sweep)}
            fill={seg.color}
            stroke="white" strokeWidth="2"
            opacity={hovered !== null && hovered !== seg.label ? 0.35 : 1}
            style={{ cursor:'pointer', transition:'opacity .12s' }}
            onMouseEnter={() => setHovered(seg.label)}
            onMouseLeave={() => setHovered(null)}
          />
        ))}
        <text x={CX} y={CY - 8} fontSize="20" fontWeight="700" textAnchor="middle"
          fill={hovSeg ? hovSeg.color : '#1e293b'}>{ctrCount}</text>
        <text x={CX} y={CY + 10} fontSize="9" fill="#64748b" textAnchor="middle">
          {hovSeg ? hovSeg.label : 'Invoices'}
        </text>
      </svg>
      <div className="db-donut-legend">
        {data.map(d => (
          <div key={d.label} className="db-donut-leg-row"
            onMouseEnter={() => setHovered(d.label)} onMouseLeave={() => setHovered(null)}
            style={{ opacity: hovered !== null && hovered !== d.label ? 0.4 : 1, transition:'opacity .12s', cursor:'default' }}>
            <span className="db-donut-swatch" style={{background: d.color}} />
            <div>
              <div className="db-donut-leg-label">{d.label} · {d.count}</div>
              <div className="db-donut-leg-amt">{fmtShort(d.amount)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopClientsChart({ data }) {
  const [hovered, setHovered] = useState(null);
  if (!data.length) return <div className="db-chart-empty">No invoices yet</div>;
  const maxVal = Math.max(...data.map(d => d.total), 1);
  return (
    <div className="db-top-clients">
      {data.map((d, i) => (
        <div key={i} className="db-tc-row"
          onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
          <div className="db-tc-name" title={d.name}>{d.name}</div>
          <div className="db-tc-bar-wrap">
            <div className="db-tc-bar"
              style={{ width: `${(d.total / maxVal) * 100}%`,
                background: hovered === i ? '#1e3a6e' : '#1e5fa8',
                transition: 'background .12s' }} />
          </div>
          <div className="db-tc-amt">{fmtShort(d.total)}</div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

function PaymentModal({ invoice, onClose, onSaved }) {
  const domesticTdsEnabled = isDomesticInvoice(invoice);
  const [form, setForm] = useState({
    payment_status: invoice.paymentStatus || 'paid',
    payment_date: invoice.paymentDate || new Date().toISOString().split('T')[0],
    payment_amount: invoice.paymentStatus === 'unpaid'
      ? ''
      : (invoice.paymentAmount || invoice.paymentAmount === 0 ? String(invoice.paymentAmount) : String(invoice.total || '')),
    payment_mode: invoice.paymentMode || 'bank_transfer',
    payment_notes: invoice.paymentNotes || '',
    tds_method: domesticTdsEnabled && invoice.tdsRate > 0 ? 'percentage' : 'receipt',
    tds_rate: invoice.tdsRate ? String(invoice.tdsRate) : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const setPaymentStatus = (value) => {
    setForm(prev => {
      if (value !== 'unpaid') return { ...prev, payment_status: value };
      return {
        ...prev,
        payment_status: 'unpaid',
        payment_amount: '',
      };
    });
  };
  const invoiceTotal = roundMoney(invoice.total);
  const manualBankReceived = roundMoney(form.payment_amount);
  const inputTdsRate = Math.max(amount(form.tds_rate), 0);
  const hasReceiptInputs = manualBankReceived > 0 || (form.payment_status !== 'unpaid' && inputTdsRate > 0);
  const canAutoTdsFromReceipt = domesticTdsEnabled && manualBankReceived > 0;
  const tdsAmount = (() => {
    if (!domesticTdsEnabled) return 0;
    if (!hasReceiptInputs && form.payment_status === 'unpaid') return 0;
    if (form.tds_method === 'percentage') {
      return roundMoney((amount(invoice.subtotal) * inputTdsRate) / 100);
    }
    if (canAutoTdsFromReceipt) {
      return roundMoney(Math.max(invoiceTotal - manualBankReceived, 0));
    }
    return 0;
  })();
  const bankReceived = (() => {
    if (form.payment_amount !== '') return manualBankReceived;
    if (domesticTdsEnabled && form.tds_method === 'percentage' && form.payment_status === 'paid') {
      return roundMoney(Math.max(invoiceTotal - tdsAmount, 0));
    }
    if (form.payment_status === 'unpaid') return 0;
    return 0;
  })();
  const settledAmount = roundMoney(bankReceived + tdsAmount);
  const balanceAmount = roundMoney(Math.max(invoiceTotal - settledAmount, 0));
  const derivedTdsRate = form.tds_method === 'percentage' ? inputTdsRate : deriveTdsRate(tdsAmount, invoice.subtotal);
  const effectivePaymentStatus = (() => {
    if (settledAmount <= 0) return 'unpaid';
    if (domesticTdsEnabled && form.tds_method === 'receipt' && manualBankReceived > 0) return 'paid';
    return balanceAmount <= 0 ? 'paid' : 'partial';
  })();
  const statusMismatch = effectivePaymentStatus !== form.payment_status;
  const autoReceiptModeNote = 'Auto mode derives TDS as Invoice Total minus Bank Amount Received, so it assumes a full settlement.';

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        payment_status: effectivePaymentStatus,
        payment_amount: effectivePaymentStatus === 'unpaid' ? 0 : bankReceived.toFixed(2),
        tds_amount: domesticTdsEnabled ? tdsAmount.toFixed(2) : 0,
      };
      const updated = await invoicesAPI.setPayment(invoice.id, payload);
      onSaved(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box payment-modal-box">
        <div className="modal-header">
          <h3 className="modal-title">Record Payment</h3>
          <div className="modal-inv-ref">{invoice.invoiceNumber} · {fmt(invoice.total, invoice.currency)}</div>
        </div>
        <form onSubmit={submit} className="modal-body payment-modal-body">
          <div className="payment-modal-layout">
            <div className="payment-modal-main">
              <div className="modal-grid3">
                <div className="modal-fg">
                  <label className="modal-lbl">Payment Status</label>
                  <select className="modal-inp" value={form.payment_status} onChange={(e) => setPaymentStatus(e.target.value)}>
                    <option value="paid">Paid in Full</option>
                    <option value="partial">Partial Payment</option>
                    <option value="unpaid">Mark as Unpaid</option>
                  </select>
                </div>
                <div className="modal-fg">
                  <label className="modal-lbl">Payment Date</label>
                  <input className="modal-inp" type="date" value={form.payment_date} onChange={(e) => set('payment_date', e.target.value)} />
                </div>
                <div className="modal-fg">
                  <label className="modal-lbl">Mode</label>
                  <select className="modal-inp" value={form.payment_mode} onChange={(e) => set('payment_mode', e.target.value)}>
                    <option value="bank_transfer">Bank Transfer / NEFT</option>
                    <option value="upi">UPI</option>
                    <option value="cheque">Cheque</option>
                    <option value="cash">Cash</option>
                    <option value="wire">International Wire</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="modal-grid3">
                <div className="modal-fg">
                  <label className="modal-lbl">Bank Amount Received</label>
                  <input
                    className="modal-inp"
                    type="number"
                    value={form.payment_amount}
                    step="0.01"
                    min="0"
                    onChange={(e) => set('payment_amount', e.target.value)}
                    placeholder="Actual bank credit"
                  />
                </div>

                {domesticTdsEnabled ? (
                  <>
                    <div className="modal-fg">
                      <label className="modal-lbl">TDS Method</label>
                      <div className="modal-method-toggle">
                        <button
                          type="button"
                          className={form.tds_method === 'receipt' ? 'active' : ''}
                          onClick={() => set('tds_method', 'receipt')}
                        >
                          Auto from Bank Received
                        </button>
                        <button
                          type="button"
                          className={form.tds_method === 'percentage' ? 'active' : ''}
                          onClick={() => set('tds_method', 'percentage')}
                        >
                          Percentage
                        </button>
                      </div>
                    </div>

                    <div className="modal-fg">
                      <label className="modal-lbl">
                        {form.tds_method === 'percentage' ? 'TDS %' : 'Computed TDS Amount'}
                      </label>
                      {form.tds_method === 'percentage' ? (
                        <input
                          className="modal-inp"
                          type="number"
                          value={form.tds_rate}
                          step="0.01"
                          min="0"
                          onChange={(e) => set('tds_rate', e.target.value)}
                          placeholder="Example: 10"
                        />
                      ) : (
                        <input
                          className="modal-inp"
                          type="text"
                          value={fmt(tdsAmount)}
                          readOnly
                        />
                      )}
                    </div>
                  </>
                ) : (
                  <div className="modal-fg modal-grid-span-2">
                    <label className="modal-lbl">TDS</label>
                    <input className="modal-inp" type="text" value="Available only for INR / domestic receipts" readOnly />
                  </div>
                )}
              </div>

              {domesticTdsEnabled && form.tds_method === 'percentage' && (
                <div className="modal-grid2">
                  <div className="modal-fg">
                    <label className="modal-lbl">Computed TDS Amount</label>
                    <input className="modal-inp" type="text" value={fmt(tdsAmount)} readOnly />
                  </div>
                  <div className="modal-fg">
                    <label className="modal-lbl">Derived TDS %</label>
                    <input className="modal-inp" type="text" value={derivedTdsRate > 0 ? `${derivedTdsRate.toFixed(2)}%` : '—'} readOnly />
                  </div>
                </div>
              )}

              <div className="modal-fg">
                <label className="modal-lbl">Payment Notes</label>
                <textarea
                  className="modal-inp modal-inp-textarea"
                  rows="4"
                  value={form.payment_notes}
                  onChange={(e) => set('payment_notes', e.target.value)}
                  placeholder="UTR / cheque no. / TDS note / certificate reference"
                />
              </div>
            </div>

            <div className="payment-modal-side">
              <div className="modal-payment-summary">
                <div className="modal-payment-chip">
                  <span>Invoice Total</span>
                  <strong>{fmt(invoice.total, invoice.currency)}</strong>
                </div>
                <div className="modal-payment-chip">
                  <span>Bank Received</span>
                  <strong>{fmt(bankReceived, invoice.currency)}</strong>
                </div>
                <div className="modal-payment-chip">
                  <span>TDS Deducted</span>
                  <strong>{fmt(tdsAmount, invoice.currency)}</strong>
                </div>
                <div className="modal-payment-chip">
                  <span>Settled Against Invoice</span>
                  <strong>{fmt(settledAmount, invoice.currency)}</strong>
                </div>
                <div className="modal-payment-chip">
                  <span>Outstanding</span>
                  <strong>{fmt(balanceAmount, invoice.currency)}</strong>
                </div>
                <div className="modal-payment-chip">
                  <span>Effective Status</span>
                  <strong>{effectivePaymentStatus === 'paid' ? 'Paid' : effectivePaymentStatus === 'partial' ? 'Partial' : 'Unpaid'}</strong>
                </div>
                <div className="modal-payment-chip">
                  <span>Derived TDS %</span>
                  <strong>{derivedTdsRate > 0 ? `${derivedTdsRate.toFixed(2)}%` : '—'}</strong>
                </div>
              </div>

              <div className="modal-help">
                {domesticTdsEnabled
                  ? (form.tds_method === 'percentage'
                    ? 'Percentage mode computes TDS on the invoice taxable value and keeps bank receipt as the actual cash received.'
                    : autoReceiptModeNote)
                  : 'TDS deduction is currently enabled only for INR / domestic invoice receipts.'}
              </div>
              {statusMismatch && (
                <div className="modal-help">
                  Based on the values entered, this payment will be saved as <strong>{effectivePaymentStatus}</strong>.
                </div>
              )}
            </div>
          </div>
          {error && <div className="modal-error">{error}</div>}
          <div className="modal-footer">
            <button type="button" className="modal-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="modal-save" disabled={saving}>{saving ? 'Saving...' : 'Save Payment'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── FY helper (Indian fiscal year Apr–Mar) ─────────────────────── */
const getIndianFY = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const m = d.getMonth() + 1;
  const y = d.getFullYear();
  const start = m >= 4 ? y : y - 1;
  return { start, label: `${start}-${String(start + 1).slice(2)}` };
};

/* ─── FY Performance Section ─────────────────────────────────────── */
function FYPerformanceSection({ invoices, fySummary }) {
  // Build FY → invoice count + invoiced total (from invoice records)
  const invByFY = useMemo(() => {
    const m = {};
    invoices.forEach(inv => {
      const fy = getIndianFY(inv.invoiceDate);
      if (!fy) return;
      if (!m[fy.label]) m[fy.label] = { label: fy.label, start: fy.start, invoiced: 0, count: 0 };
      m[fy.label].invoiced += amount(inv.total);
      m[fy.label].count++;
    });
    return m;
  }, [invoices]);

  // Build rows from ledger fySummary (credits = actual collected, debits = expenses)
  const fyRows = useMemo(() => {
    const allLabels = new Set([
      ...Object.keys(invByFY),
      ...(fySummary || []).map(r => r.fy_label),
    ]);
    return [...allLabels]
      .map(label => {
        const inv  = invByFY[label]  || { invoiced: 0, count: 0 };
        const ledg = (fySummary || []).find(r => r.fy_label === label) || { credits: 0, debits: 0 };
        const collected = ledg.credits;   // actual bank credits (receipts)
        const expenses  = ledg.debits;    // actual bank debits (outgoings)
        const net       = collected - expenses;
        const start = inv.start || parseInt(label.split('-')[0], 10);
        return { label, start, invoiced: inv.invoiced, collected, expenses, net, count: inv.count };
      })
      .sort((a, b) => a.start - b.start);
  }, [invByFY, fySummary]);

  if (!fyRows.length) return null;

  const totalCollected = fyRows.reduce((s, r) => s + r.collected, 0);
  const totalExpenses  = fyRows.reduce((s, r) => s + r.expenses, 0);
  const totalNet       = totalCollected - totalExpenses;
  const avgTurnover    = fyRows.length ? totalCollected / fyRows.length : 0;

  // Sparkline bar for each FY row (collected vs expenses)
  const maxBar = Math.max(...fyRows.map(r => Math.max(r.collected, r.expenses, 1)));

  const fmtL = v => {
    const a = Math.abs(v);
    return (v < 0 ? '−' : '') + (a >= 1e7 ? `₹${(a/1e7).toFixed(2)}Cr` : a >= 1e5 ? `₹${(a/1e5).toFixed(2)}L` : `₹${a.toLocaleString('en-IN', {maximumFractionDigits:0})}`);
  };

  return (
    <div className="db-section db-fy-section">
      <div className="db-section-head">
        <span>Financial Year Performance</span>
        <span className="db-fy-avg-label">Net Avg Turnover <strong>{fmtL(avgTurnover)}/yr</strong></span>
      </div>

      {/* Summary KPIs */}
      <div className="db-fy-kpis">
        <div className="db-fy-kpi">
          <div className="db-fy-kpi-label">Total Invoiced</div>
          <div className="db-fy-kpi-val">{fmtL(fyRows.reduce((s,r)=>s+r.invoiced,0))}</div>
        </div>
        <div className="db-fy-kpi">
          <div className="db-fy-kpi-label">Total Collected</div>
          <div className="db-fy-kpi-val collected">{fmtL(totalCollected)}</div>
        </div>
        <div className="db-fy-kpi">
          <div className="db-fy-kpi-label">Total Expenses</div>
          <div className="db-fy-kpi-val expenses">{fmtL(totalExpenses)}</div>
        </div>
        <div className="db-fy-kpi">
          <div className="db-fy-kpi-label">Cumulative Net</div>
          <div className={`db-fy-kpi-val ${totalNet >= 0 ? 'positive' : 'negative'}`}>{fmtL(totalNet)}</div>
        </div>
      </div>

      {/* FY table */}
      <div className="db-fy-table-wrap">
        <table className="db-fy-table">
          <thead>
            <tr>
              <th>FY</th>
              <th>Inv.</th>
              <th className="th-r">Invoiced</th>
              <th className="th-r">Collected</th>
              <th className="th-r">Expenses</th>
              <th className="th-r">Net</th>
              <th className="th-bar">vs Expenses</th>
            </tr>
          </thead>
          <tbody>
            {fyRows.map(r => {
              const barCW = maxBar > 0 ? Math.round((r.collected / maxBar) * 80) : 0;
              const barEW = maxBar > 0 ? Math.round((r.expenses  / maxBar) * 80) : 0;
              return (
                <tr key={r.label} className="db-fy-row">
                  <td><strong>{r.label}</strong></td>
                  <td className="db-fy-count">{r.count}</td>
                  <td className="td-r">{fmtL(r.invoiced)}</td>
                  <td className="td-r collected">{fmtL(r.collected)}</td>
                  <td className="td-r expenses">{fmtL(r.expenses)}</td>
                  <td className={`td-r ${r.net >= 0 ? 'positive' : 'negative'}`}><strong>{fmtL(r.net)}</strong></td>
                  <td className="td-bars">
                    <svg width="88" height="20" viewBox="0 0 88 20">
                      <rect x="0" y="2"  width={barCW} height="7" rx="2" fill="#10b981" opacity="0.85"/>
                      <rect x="0" y="11" width={barEW} height="7" rx="2" fill="#f43f5e" opacity="0.7"/>
                    </svg>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="db-fy-legend">
          <span className="db-fy-leg collected">■ Collected</span>
          <span className="db-fy-leg expenses">■ Expenses</span>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({ onNewInvoice, onLoadInvoice, onOpenContracts }) {
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [fySummary, setFySummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payModal, setPayModal] = useState(null);
  const [invPage, setInvPage] = useState(1);
  const INV_PAGE_SIZE = 10;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [invoiceRows, contractRows, fyData] = await Promise.all([
        invoicesAPI.list(),
        contractsAPI.list(),
        transactionsAPI.fySummary().catch(() => []),
      ]);
      setInvoices(invoiceRows.map(normalizeInvoice));
      setContracts(contractRows);
      setFySummary(fyData);
    } catch {
      setInvoices([]);
      setContracts([]);
      setFySummary([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ── Chart data ──────────────────────────── */
  const monthlyData = useMemo(() => {
    const buckets = {};
    invoices.forEach(inv => {
      if (!inv.invoiceDate) return;
      const d = new Date(inv.invoiceDate);
      if (Number.isNaN(d.getTime())) return;
      const key   = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      const label = MONTH_LABELS[d.getMonth()];
      if (!buckets[key]) buckets[key] = { key, label, invoiced: 0, collected: 0 };
      buckets[key].invoiced  += amount(inv.total);
      buckets[key].collected += amount(inv.paymentAmount) + amount(inv.tdsAmount);
    });
    return Object.values(buckets)
      .sort((a, b) => a.key.localeCompare(b.key))
      .slice(-6);
  }, [invoices]);

  const statusData = useMemo(() => {
    const groups = {
      paid:    { label:'Paid',    color:'#10b981', count:0, amount:0 },
      partial: { label:'Partial', color:'#f59e0b', count:0, amount:0 },
      unpaid:  { label:'Unpaid',  color:'#ef4444', count:0, amount:0 },
    };
    invoices.forEach(inv => {
      const g = groups[inv.paymentStatus] || groups.unpaid;
      g.count++;
      g.amount += amount(inv.total);
    });
    return Object.values(groups);
  }, [invoices]);

  const topClients = useMemo(() => {
    const clients = {};
    invoices.forEach(inv => {
      const name = inv.clientName || 'Unknown';
      if (!clients[name]) clients[name] = { name, total: 0 };
      clients[name].total += amount(inv.total);
    });
    return Object.values(clients).sort((a, b) => b.total - a.total).slice(0, 5);
  }, [invoices]);
  /* ─────────────────────────────────────────── */

  const activeContracts = contracts.filter(contract => contract.status === 'active');
  const draftContracts = contracts.filter(contract => contract.status === 'draft');
  const expiringSoonContracts = activeContracts.filter(contract => isExpiringSoon(contract));
  const mixedCurrencies = new Set(contracts.map(contract => contract.billing_currency || 'INR')).size > 1;

  const totalPortfolio = contracts.reduce((sum, contract) => sum + currentValueFor(contract), 0);
  const totalInvoiced = contracts.reduce((sum, contract) => sum + invoicedAmountFor(contract), 0);
  const totalRemaining = contracts.reduce((sum, contract) => sum + remainingValueFor(contract), 0);
  const setupNeededContracts = activeContracts.filter(contract =>
    !Number(contract.milestone_count) || !Number(contract.deliverable_count) || !Number(contract.document_count)
  );

  const gapRows = [
    {
      label: 'Missing milestones',
      value: activeContracts.filter(contract => !Number(contract.milestone_count)).length,
      hint: 'Active contracts with no billing milestones mapped yet.',
    },
    {
      label: 'Missing deliverables',
      value: activeContracts.filter(contract => !Number(contract.deliverable_count)).length,
      hint: 'Execution outputs are not yet linked to an invoice plan.',
    },
    {
      label: 'Missing documents',
      value: activeContracts.filter(contract => !Number(contract.document_count)).length,
      hint: 'Audit and donor records still need to be registered.',
    },
    {
      label: 'Draft contracts',
      value: draftContracts.length,
      hint: 'Contracts still waiting for final data before going live.',
    },
  ];

  const portfolioContracts = [...activeContracts]
    .map(contract => ({
      ...contract,
      currentValue: currentValueFor(contract),
      invoicedValue: invoicedAmountFor(contract),
      remainingValue: remainingValueFor(contract),
      billedPct: billedPctFor(contract),
    }))
    .sort((a, b) => b.currentValue - a.currentValue)
    .slice(0, 6);

  const attentionContracts = contracts
    .map(contract => {
      const flags = attentionFlagsFor(contract);
      const remainingValue = remainingValueFor(contract);
      return {
        ...contract,
        flags,
        remainingValue,
        currentValue: currentValueFor(contract),
        invoicedValue: invoicedAmountFor(contract),
        billedPct: billedPctFor(contract),
        attentionScore:
          (flags.includes('Over ceiling') ? 50 : 0) +
          (flags.includes('Renew soon') ? 30 : 0) +
          (flags.includes('Draft contract') ? 10 : 0) +
          flags.length,
      };
    })
    .filter(contract => contract.flags.length > 0)
    .sort((a, b) => {
      if (b.attentionScore !== a.attentionScore) return b.attentionScore - a.attentionScore;
      return (daysUntil(a.revised_end_date || a.end_date) ?? 9999) - (daysUntil(b.revised_end_date || b.end_date) ?? 9999);
    })
    .slice(0, 8);

  const renewalContracts = [...activeContracts]
    .filter(contract => contract.revised_end_date || contract.end_date)
    .sort((a, b) => (daysUntil(a.revised_end_date || a.end_date) ?? 9999) - (daysUntil(b.revised_end_date || b.end_date) ?? 9999))
    .slice(0, 5);

  const handlePaymentSaved = (updated) => {
    setInvoices(prev => prev.map(invoice => invoice.id === updated.id ? normalizeInvoice(updated) : invoice));
    setPayModal(null);
    load();
  };

  if (loading) return <div className="db-loading">Loading dashboard...</div>;

  return (
    <div className="db-page">
      <div className="db-hero">
        <div className="db-hero-copy">
          <div className="db-hero-title">Contract Portfolio</div>
          <div className="db-hero-sub">
            Track contract value, setup readiness, renewals, and billing progress from one place.
          </div>
        </div>
        <div className="db-hero-actions">
          <button className="db-hero-btn ghost" onClick={() => onOpenContracts?.()}>Open Contracts</button>
          <button className="db-hero-btn solid" onClick={() => onNewInvoice(portfolioContracts[0] || null)}>+ New Invoice</button>
        </div>
      </div>

      <div className="db-kpi-row db-kpi-row-compact">
        {[
          {
            cls: 'db-kpi-activecontracts',
            label: 'Active Contracts',
            value: String(activeContracts.length),
            sub: `${draftContracts.length} draft · ${contracts.length} total`,
          },
          {
            cls: 'db-kpi-portfolio',
            label: 'Portfolio Value',
            value: fmtShort(totalPortfolio),
            sub: mixedCurrencies ? 'Mixed contract currencies' : 'Current contract ceiling',
          },
          {
            cls: 'db-kpi-invoiced',
            label: 'Invoiced',
            value: fmtShort(totalInvoiced),
            sub: mixedCurrencies ? 'Mixed invoice currencies' : 'Across all linked contracts',
          },
          {
            cls: totalRemaining < 0 ? 'db-kpi-overrun' : 'db-kpi-unbilled',
            label: totalRemaining < 0 ? 'Overrun' : 'Remaining Value',
            value: fmtShort(totalRemaining),
            sub: totalRemaining < 0 ? 'Invoices above current ceiling' : 'Still available to bill',
          },
          {
            cls: setupNeededContracts.length > 0 ? 'db-kpi-setup' : 'db-kpi-ready',
            label: 'Needs Setup',
            value: String(setupNeededContracts.length),
            sub: `${expiringSoonContracts.length} expiring in 45 days`,
          },
        ].map(card => (
          <div key={card.label} className={`db-kpi ${card.cls}`}>
            <div className="db-kpi-icon-wrap">
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1">
                {card.label === 'Active Contracts' && (
                  <>
                    <path d="M9 17H7A5 5 0 0 1 7 7h2" />
                    <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </>
                )}
                {card.label === 'Portfolio Value' && (
                  <>
                    <path d="M3 7h18v10H3z" />
                    <path d="M7 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
                    <path d="M3 12h18" />
                  </>
                )}
                {card.label === 'Invoiced' && (
                  <>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </>
                )}
                {(card.label === 'Remaining Value' || card.label === 'Overrun') && (
                  <>
                    <line x1="12" y1="5" x2="12" y2="19" />
                    {card.label === 'Overrun'
                      ? <polyline points="5 12 12 5 19 12" />
                      : <polyline points="19 12 12 19 5 12" />}
                  </>
                )}
                {card.label === 'Needs Setup' && (
                  <>
                    <circle cx="12" cy="12" r="9" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </>
                )}
              </svg>
            </div>
            <div>
              <div className="db-kpi-label">{card.label}</div>
              <div className="db-kpi-value">{card.value}</div>
              <div className="db-kpi-sub">{card.sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── FY Performance ── */}
      <FYPerformanceSection invoices={invoices} fySummary={fySummary} />

      {/* ── Financial Performance Charts ──────────── */}
      <div className="db-charts-row">
        <div className="db-chart-card">
          <div className="db-chart-head">
            <span>Monthly Revenue</span>
            <MonthlyBarLegend />
          </div>
          <MonthlyBarChart data={monthlyData} />
        </div>
        <div className="db-chart-card">
          <div className="db-chart-head">
            <span>Invoice Payment Status</span>
          </div>
          <StatusDonut data={statusData} />
        </div>
        <div className="db-chart-card">
          <div className="db-chart-head">
            <span>Top Clients</span>
            <span className="db-chart-sub">by invoiced value</span>
          </div>
          <TopClientsChart data={topClients} />
        </div>
      </div>

      <div className="db-overview-grid">
        <div className="db-section">
          <div className="db-section-head">
            <span>Portfolio Overview</span>
            <button className="db-section-action" onClick={() => onOpenContracts?.()}>View All</button>
          </div>
          {portfolioContracts.length === 0 ? (
            <div className="db-empty-sm">No active contracts yet. Add a contract to start managing billing and deliverables.</div>
          ) : (
            <div className="db-portfolio-list">
              {portfolioContracts.map(contract => (
                <div key={contract.id} className="db-portfolio-row">
                  <div className="db-portfolio-main">
                    <div className="db-portfolio-top">
                      <div>
                        <div className="db-portfolio-name">{contract.project_name}</div>
                        <div className="db-portfolio-sub">
                          {contract.client_name}
                          {contract.donor_name ? ` · ${contract.donor_name}` : ''}
                        </div>
                      </div>
                      <button className="db-portfolio-action" onClick={() => onNewInvoice(contract)}>+ Invoice</button>
                    </div>
                    <div className="db-portfolio-meta">
                      {fmtDate(contract.start_date, 'Start TBD')} -> {fmtDate(contract.revised_end_date || contract.end_date)}
                      {' · '}
                      {contract.milestone_count || 0} milestones
                      {' · '}
                      {contract.deliverable_count || 0} deliverables
                      {' · '}
                      {contract.document_count || 0} docs
                    </div>
                    <div className="db-portfolio-bar-wrap">
                      <div className="db-portfolio-bar" style={{ width: `${contract.billedPct}%` }} />
                    </div>
                  </div>
                  <div className="db-portfolio-side">
                    <strong>{fmt(contract.currentValue, contract.billing_currency || 'INR')}</strong>
                    <span>{fmt(contract.invoicedValue, contract.billing_currency || 'INR')} billed</span>
                    <span className={contract.remainingValue < 0 ? 'danger' : ''}>
                      {fmt(contract.remainingValue, contract.billing_currency || 'INR')} remaining
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="db-side-stack">
          <div className="db-indicator-card">
            <div className="db-ind-title">Execution Setup Gaps</div>
            <div className="db-gap-list">
              {gapRows.map(row => (
                <div key={row.label} className="db-gap-row">
                  <div>
                    <div className="db-gap-label">{row.label}</div>
                    <div className="db-gap-hint">{row.hint}</div>
                  </div>
                  <div className={`db-gap-value${row.value > 0 ? ' warn' : ''}`}>{row.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="db-indicator-card">
            <div className="db-ind-title">Renewals & Closures</div>
            {renewalContracts.length === 0 ? (
              <div className="db-ind-empty">No contract end dates recorded yet.</div>
            ) : (
              <div className="db-renewal-list">
                {renewalContracts.map(contract => {
                  const days = daysUntil(contract.revised_end_date || contract.end_date);
                  return (
                    <div key={contract.id} className="db-renewal-row">
                      <div>
                        <div className="db-renewal-name">{contract.project_name}</div>
                        <div className="db-renewal-sub">{contract.client_name}</div>
                      </div>
                      <div className="db-renewal-side">
                        <strong>{fmtDate(contract.revised_end_date || contract.end_date)}</strong>
                        <span className={days !== null && days <= 45 ? 'warn' : ''}>
                          {days === null ? 'No end date' : days === 0 ? 'Ends today' : `${days} days left`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="db-section">
        <div className="db-section-head">
          <span>Contracts Needing Attention</span>
          <span className="db-count">{attentionContracts.length}</span>
        </div>
        {attentionContracts.length === 0 ? (
          <div className="db-empty-sm">No contract issues detected right now. Your active portfolio is fully set up.</div>
        ) : (
          attentionContracts.map(contract => (
            <div key={contract.id} className="db-contract-attn-row">
              <div className="db-contract-attn-main">
                <div className="db-contract-attn-top">
                  <div className="db-contract-attn-name">{contract.project_name}</div>
                  <div className="db-contract-attn-flags">
                    {contract.flags.map(flag => (
                      <span key={flag} className={`db-attn-flag${flag === 'Over ceiling' ? ' danger' : flag === 'Renew soon' ? ' warning' : ''}`}>
                        {flag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="db-contract-attn-sub">
                  {contract.client_name}
                  {contract.contract_number ? ` · #${contract.contract_number}` : ''}
                  {contract.donor_name ? ` · ${contract.donor_name}` : ''}
                </div>
                <div className="db-contract-attn-meta">
                  {fmtDate(contract.revised_end_date || contract.end_date)}
                  {' · '}
                  {contract.milestone_count || 0} milestones
                  {' · '}
                  {contract.deliverable_count || 0} deliverables
                  {' · '}
                  {contract.document_count || 0} docs
                </div>
                <div className="db-portfolio-bar-wrap slim">
                  <div className="db-portfolio-bar" style={{ width: `${contract.billedPct}%` }} />
                </div>
              </div>
              <div className="db-contract-attn-side">
                <strong>{fmt(contract.currentValue, contract.billing_currency || 'INR')}</strong>
                <span>{fmt(contract.invoicedValue, contract.billing_currency || 'INR')} billed</span>
                <span className={contract.remainingValue < 0 ? 'danger' : ''}>
                  {fmt(contract.remainingValue, contract.billing_currency || 'INR')} remaining
                </span>
                <button className="db-cr-inv-btn" onClick={() => onNewInvoice(contract)}>+ Invoice</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Recent Invoices (paginated) ── */}
      {(() => {
        const totalInvPages = Math.max(1, Math.ceil(invoices.length / INV_PAGE_SIZE));
        const safePage = Math.min(invPage, totalInvPages);
        const pageSlice = invoices.slice((safePage - 1) * INV_PAGE_SIZE, safePage * INV_PAGE_SIZE);
        return (
          <div className="db-section">
            <div className="db-section-head">
              <span>Invoices <span className="db-count">{invoices.length}</span></span>
              <button className="db-section-action" onClick={() => onNewInvoice(null)}>+ New Invoice</button>
            </div>
            {invoices.length === 0 ? (
              <div className="db-empty-sm">No invoices yet. Create your first invoice from a contract or click + New Invoice.</div>
            ) : (
              <>
                {pageSlice.map(invoice => (
                  <div key={invoice.id} className="db-inv-row">
                    <div className="db-inv-main">
                      <span className="db-inv-num">{invoice.invoiceNumber}</span>
                      <span className="db-inv-client">{invoice.clientName || invoice.agreementNumber || '—'}</span>
                    </div>
                    <div className="db-inv-right">
                      <span className="db-inv-date">{fmtDate(invoice.invoiceDate, '')}</span>
                      <span className="db-inv-amt">{fmt(invoice.total, invoice.currency)}</span>
                      <span className={`db-pay-badge ${PAYMENT_COLORS[invoice.paymentStatus] || 'pay-unpaid'}`}>
                        {PAYMENT_LABELS[invoice.paymentStatus] || invoice.paymentStatus}
                      </span>
                      <button className="db-inv-pay-btn" onClick={() => setPayModal(invoice)} title="Record payment">
                        {invoice.paymentStatus === 'paid' ? '✓' : '₹'}
                      </button>
                      <button className="db-inv-edit-btn" onClick={() => onLoadInvoice(invoice)} title="Open invoice">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
                {totalInvPages > 1 && (
                  <div className="db-inv-pagination">
                    <button className="pg-btn" disabled={safePage === 1} onClick={() => setInvPage(1)}>«</button>
                    <button className="pg-btn" disabled={safePage === 1} onClick={() => setInvPage(p => p - 1)}>‹</button>
                    <span className="pg-info">
                      Page <strong>{safePage}</strong> of <strong>{totalInvPages}</strong>
                      <span className="pg-sub">({invoices.length} invoices)</span>
                    </span>
                    <button className="pg-btn" disabled={safePage === totalInvPages} onClick={() => setInvPage(p => p + 1)}>›</button>
                    <button className="pg-btn" disabled={safePage === totalInvPages} onClick={() => setInvPage(totalInvPages)}>»</button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

      {payModal && <PaymentModal invoice={payModal} onClose={() => setPayModal(null)} onSaved={handlePaymentSaved} />}
    </div>
  );
}
